import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { ArrowLeft, Lock, Check, ChevronRight, Sparkles } from "lucide-react";

export default function MayaManthanPath() {
  const navigate = useNavigate();
  const [levels, setLevels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLevel, setSelectedLevel] = useState(null);

  useEffect(() => {
    fetchLevels();
  }, []);

  const fetchLevels = async () => {
    try {
      const response = await axios.get(`${API}/maya-manthan/levels`);
      setLevels(response.data);
    } catch (error) {
      console.error("Error fetching levels:", error);
      toast.error("Failed to load transformation path");
    } finally {
      setLoading(false);
    }
  };

  const fetchLevelDetails = async (levelNum) => {
    try {
      const response = await axios.get(`${API}/maya-manthan/levels/${levelNum}`);
      setSelectedLevel(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("This level is not unlocked yet");
      } else {
        toast.error("Failed to load level details");
      }
    }
  };

  const levelColors = [
    "from-red-600 to-red-800",
    "from-orange-500 to-orange-700",
    "from-yellow-500 to-yellow-700",
    "from-green-500 to-green-700",
    "from-purple-500 to-purple-700"
  ];

  const levelIcons = ["🌱", "👁️", "💨", "⚡", "🕉️"];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a1a] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-amber-500/30 border-t-amber-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a1a] relative" data-testid="maya-path">
      <div className="fixed inset-0 bg-gradient-to-b from-[#0d0d2b] via-[#0a0a1a] to-[#050510]" />

      {/* Back Button */}
      <button
        onClick={() => navigate('/maya-manthan')}
        className="fixed top-6 left-6 z-50 p-2 rounded-full bg-white/5 hover:bg-white/10 transition-colors"
      >
        <ArrowLeft className="w-5 h-5 text-gray-400" />
      </button>

      <div className="relative z-10 max-w-4xl mx-auto px-6 py-16">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="font-serif text-4xl text-white mb-4">Your Transformation Path</h1>
          <p className="text-gray-400">Progress through five levels of inner awakening</p>
        </motion.div>

        {/* Path Visualization */}
        <div className="relative">
          {/* Connecting Line */}
          <div className="absolute left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-white/20 to-transparent -translate-x-1/2 hidden md:block" />

          {/* Levels */}
          <div className="space-y-8">
            {levels.map((level, index) => (
              <motion.div
                key={level.level}
                initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`md:flex md:items-center md:gap-8 ${
                  index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                }`}
              >
                {/* Level Card */}
                <div 
                  onClick={() => level.unlocked && fetchLevelDetails(level.level)}
                  className={`flex-1 bg-[#0d0d2b]/60 backdrop-blur-xl border rounded-2xl p-6 cursor-pointer transition-all ${
                    level.current 
                      ? 'border-amber-500/50 ring-2 ring-amber-500/20' 
                      : level.completed 
                      ? 'border-green-500/30' 
                      : level.unlocked 
                      ? 'border-white/10 hover:border-white/20' 
                      : 'border-white/5 opacity-50'
                  }`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${levelColors[index]} flex items-center justify-center text-2xl flex-shrink-0`}>
                      {level.unlocked ? levelIcons[index] : <Lock className="w-6 h-6 text-white/50" />}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-gray-500 text-sm">Level {level.level}</span>
                        {level.completed && <Check className="w-4 h-4 text-green-400" />}
                        {level.current && <Sparkles className="w-4 h-4 text-amber-400" />}
                      </div>
                      <h3 className="text-white font-serif text-xl mb-2">{level.name}</h3>
                      <p className="text-gray-400 text-sm">{level.description}</p>
                      
                      {level.unlocked && (
                        <button className="mt-3 text-amber-400 text-sm flex items-center gap-1 hover:text-amber-300">
                          View Details <ChevronRight className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Center Dot (Desktop) */}
                <div className="hidden md:flex items-center justify-center w-12 flex-shrink-0">
                  <div className={`w-4 h-4 rounded-full ${
                    level.completed ? 'bg-green-500' :
                    level.current ? 'bg-amber-500 animate-pulse' :
                    level.unlocked ? 'bg-white/30' : 'bg-white/10'
                  }`} />
                </div>

                {/* Spacer */}
                <div className="flex-1 hidden md:block" />
              </motion.div>
            ))}
          </div>
        </div>

        {/* Level Details Modal */}
        {selectedLevel && (
          <div 
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
            onClick={() => setSelectedLevel(null)}
          >
            <motion.div 
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="bg-[#0d0d2b] border border-white/10 rounded-2xl p-6 max-w-md w-full max-h-[80vh] overflow-auto"
              onClick={e => e.stopPropagation()}
            >
              <h3 className="font-serif text-2xl text-white mb-2">{selectedLevel.name}</h3>
              <p className="text-gray-400 text-sm mb-6">{selectedLevel.description}</p>

              {/* Progress */}
              <div className="mb-6">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-500">Progress</span>
                  <span className="text-white">{selectedLevel.progress_percentage}%</span>
                </div>
                <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-amber-500"
                    style={{ width: `${selectedLevel.progress_percentage}%` }}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {selectedLevel.sessions_completed} / {selectedLevel.sessions_required} sessions
                </p>
              </div>

              {/* Practices */}
              <div className="mb-6">
                <h4 className="text-white text-sm font-medium mb-3">Practices</h4>
                <div className="space-y-2">
                  {selectedLevel.practices?.map((practice, i) => (
                    <div key={i} className="bg-white/5 rounded-lg p-3 flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-indigo-500/20 flex items-center justify-center">
                        <span className="text-sm">🧘</span>
                      </div>
                      <span className="text-gray-300 text-sm capitalize">{practice.replace(/_/g, ' ')}</span>
                    </div>
                  ))}
                </div>
              </div>

              <Button
                onClick={() => setSelectedLevel(null)}
                className="w-full bg-amber-500 hover:bg-amber-600 text-black"
              >
                Close
              </Button>
            </motion.div>
          </div>
        )}
      </div>
    </div>
  );
}
